// extVersions/reducer.js
import { sanitizeId } from "../../commonUtils/extraction.helper";
import constants from "../../commonUtils/constants";
import * as actionTypes from "./actionTypes";

const initialState = {
    loading: false,
    downloading: false,
    singleVersionLoading: false,
    filters: { keyword: "" },
    ExtVersionsList: constants.extraction.listInitialState,
    extVersion: constants.extraction.initialState.extVersion,
    openModal: false,
    errors: {},
    errorMessage: "",
    // Polling state removed — now lives entirely in ExtVersionsContext
};

const ExtVersions = (state = initialState, action) => {
    switch (action.type) {

        // ── Loading flags ────────────────────────────────────────────────
        case actionTypes.SET_LOADING:
            return { ...state, loading: action.payload };

        case actionTypes.SET_DOWNLOADING:
            return { ...state, downloading: action.payload };

        case actionTypes.SET_SINGLE_VERSION_LOADING:
            return { ...state, singleVersionLoading: action.payload };

        // ── Misc ─────────────────────────────────────────────────────────
        case actionTypes.SET_ERRORS:
            return { ...state, errors: action.payload };

        case actionTypes.SET_FILTERS:
            return { ...state, filters: action.payload };

        case actionTypes.SET_OPEN_MODAL:
            return {
                ...state,
                openModal: action.payload,
                loading: action.payload ? state.loading : false,
            };

        case actionTypes.SET_EXT_VERSION: {
            const newExtVersion = sanitizeId({ ...state.extVersion, ...action.payload });
            return { ...state, extVersion: newExtVersion };
        }

        case actionTypes.RESET_EXT_VERSION:
            return {
                ...state,
                extVersion: constants.extraction.initialState.extVersion,
                singleVersionLoading: false,
                errors: {},
                errorMessage: "",
            };

        // ── GET ALL ──────────────────────────────────────────────────────
        case actionTypes.GET_EXT_VERSIONS_SUCCESS:
            return {
                ...state,
                loading: false,
                ExtVersionsList: action.payload,
                errors: {},
                errorMessage: "",
            };

        case actionTypes.GET_EXT_VERSIONS_FAILURE:
            return {
                ...state,
                loading: false,
                errors: action.payload.data || {},
                errorMessage: action.payload.message || "Failed to fetch versions",
            };

        // ── GET BY KEY ───────────────────────────────────────────────────
        case actionTypes.GET_EXT_VERSION_BY_KEY_SUCCESS:
            return {
                ...state,
                loading: false,
                extVersion: sanitizeId(action.payload),
                errors: {},
                errorMessage: "",
            };

        case actionTypes.GET_EXT_VERSION_BY_KEY_FAILURE:
            return {
                ...state,
                loading: false,
                openModal: false,
                errors: action.payload.data || {},
                errorMessage: action.payload.message || "Failed to fetch version",
            };

        case actionTypes.GET_EXT_VERSION_FAILURE:
            return {
                ...state,
                loading: false,
                errors: action.payload.data || {},
                errorMessage: action.payload.message || "Failed to fetch version",
            };

        // ── CREATE ───────────────────────────────────────────────────────
        case actionTypes.CREATE_EXT_VERSION_SUCCESS: {
            const newVersion = sanitizeId(action.payload);
            return {
                ...state,
                singleVersionLoading: false,
                openModal: false,
                extVersion: newVersion,
                errors: {},
                errorMessage: "",
            };
        }

        case actionTypes.CREATE_EXT_VERSION_FAILURE:
            return {
                ...state,
                loading: false,
                singleVersionLoading: false,
                errors: action.payload.errors || {},
                errorMessage: action.payload.message || "Failed to create version",
            };

        // ── BATCH STATUS CHECK ───────────────────────────────────────────
        // Fired by the context setInterval — silently patches status fields
        // on every row that was returned by the backend batch check.
        case actionTypes.BATCH_STATUS_CHECK_SUCCESS: {
            const updatedVersions = action.payload; // flat array from backend
            if (!state.ExtVersionsList.data) return state;

            // If batch returns empty array, it means NO versions are processing
            // Clear processing status from all currently-processing versions
            if (!updatedVersions?.length) {
                const PROCESSING_STATUSES = ['pending', 'triggered', 'processing'];
                const newListData = state.ExtVersionsList.data.map((row) => {
                    const currentStatus = (row.process_status || row.status || '').toLowerCase();
                    // Only update if currently marked as processing
                    if (!PROCESSING_STATUSES.includes(currentStatus)) {
                        return row; // Not processing, leave untouched
                    }
                    // Mark as no longer processing by clearing process_status
                    return {
                        ...row,
                        process_status: null,
                    };
                });

                return {
                    ...state,
                    ExtVersionsList: { ...state.ExtVersionsList, data: newListData },
                };
            }

            // Build a lookup map for O(1) access
            const updateMap = new Map(
                updatedVersions.map((v) => [String(v._id), v])
            );

            const newListData = state.ExtVersionsList.data.map((row) => {
                const update = updateMap.get(String(row._id));
                if (!update) return row; // row not in batch — leave untouched

                return {
                    ...row,
                    status:                  update.status                  ?? row.status,
                    process_status:          update.process_status          ?? row.process_status,
                    process_exec_id:         update.process_exec_id         ?? row.process_exec_id,
                    process_start_time:      update.process_start_time      ?? row.process_start_time,
                    process_completion_time: update.process_completion_time ?? row.process_completion_time,
                    process_run_duration:    update.process_run_duration    ?? row.process_run_duration,
                    output:                  update.output !== undefined     ? update.output : row.output,
                    error_message:           update.error_message           ?? row.error_message,
                };
            });

            return {
                ...state,
                ExtVersionsList: { ...state.ExtVersionsList, data: newListData },
            };
        }

        // ── RETRY OUTPUT (manual single-version check) ───────────────────
        case actionTypes.RETRY_EXT_VERSION_OUTPUT_REQUEST: {
            const requestListData = state.ExtVersionsList.data
                ? state.ExtVersionsList.data.map((v) =>
                      v._id === action.id || v.id === action.id
                          ? { ...v, loading: true }
                          : v
                  )
                : state.ExtVersionsList.data;

            return {
                ...state,
                extVersion:
                    state.extVersion?._id === action.id || state.extVersion?.id === action.id
                        ? { ...state.extVersion, loading: true }
                        : state.extVersion,
                ExtVersionsList: { ...state.ExtVersionsList, data: requestListData },
            };
        }

        case actionTypes.RETRY_EXT_VERSION_OUTPUT_SUCCESS: {
            const retryResponse  = action.payload;
            const versionUpdate  = retryResponse?.extVersion || retryResponse || {};
            const canonicalId    = versionUpdate._id || versionUpdate.id || null;

            const updatedListData = state.ExtVersionsList.data
                ? state.ExtVersionsList.data.map((v) => {
                      if (!canonicalId) return v;
                      const rowId = v._id || v.id;
                      if (!rowId || rowId !== canonicalId) return v;
                      return {
                          ...v,
                          ...versionUpdate,
                          status:                  versionUpdate.status || versionUpdate.process_status || v.status,
                          process_status:          versionUpdate.process_status || v.process_status,
                          process_exec_id:         versionUpdate.process_exec_id         || v.process_exec_id,
                          process_start_time:      versionUpdate.process_start_time      || v.process_start_time,
                          process_completion_time: versionUpdate.process_completion_time || v.process_completion_time,
                          process_run_duration:    versionUpdate.process_run_duration    || v.process_run_duration,
                          output:                  versionUpdate.output !== undefined ? versionUpdate.output : v.output,
                          error_message:           versionUpdate.error_message || v.error_message,
                          loading: false,
                      };
                  })
                : state.ExtVersionsList.data;

            const shouldUpdateExtVersion =
                canonicalId &&
                state.extVersion &&
                (state.extVersion.id === canonicalId || state.extVersion._id === canonicalId);

            return {
                ...state,
                extVersion: shouldUpdateExtVersion
                    ? sanitizeId({ ...state.extVersion, ...versionUpdate, loading: false })
                    : state.extVersion,
                ExtVersionsList: { ...state.ExtVersionsList, data: updatedListData },
                errors: {},
                errorMessage: "",
            };
        }

        case actionTypes.RETRY_EXT_VERSION_OUTPUT_FAILURE: {
            const failedRetryListData = state.ExtVersionsList.data
                ? state.ExtVersionsList.data.map((v) => {
                      const targetId = action.id;
                      const matches =
                          v._id === targetId ||
                          v.id  === targetId ||
                          (!targetId && (
                              v._id === state.extVersion?._id ||
                              v._id === state.extVersion?.id  ||
                              v.id  === state.extVersion?.id
                          ));
                      return matches ? { ...v, loading: false } : v;
                  })
                : state.ExtVersionsList.data;

            return {
                ...state,
                extVersion: state.extVersion
                    ? sanitizeId({ ...state.extVersion, loading: false })
                    : state.extVersion,
                ExtVersionsList: { ...state.ExtVersionsList, data: failedRetryListData },
                errors:       action.payload.data || {},
                errorMessage: action.payload.message || "Failed to retry output",
            };
        }

        // ── UPDATE ───────────────────────────────────────────────────────
        case actionTypes.UPDATE_EXT_VERSION_SUCCESS:
            return { ...state, loading: false, openModal: false, errors: {}, errorMessage: "" };

        case actionTypes.UPDATE_EXT_VERSION_FAILURE:
            return {
                ...state,
                loading: false,
                errors:       action.payload.errors || {},
                errorMessage: action.payload.message || "Failed to update version",
            };

        case actionTypes.UPDATE_EXT_VERSIONS_ORDER_SUCCESS:
            return { ...state, errors: {}, errorMessage: "" };

        case actionTypes.UPDATE_EXT_VERSIONS_ORDER_FAILURE:
            return {
                ...state,
                errors:       action.payload.data || {},
                errorMessage: action.payload.message || "Failed to update order",
            };

        // ── DELETE ───────────────────────────────────────────────────────
        case actionTypes.DELETE_EXT_VERSION_SUCCESS:
            return { ...state, loading: false, errors: {}, errorMessage: "" };

        case actionTypes.DELETE_EXT_VERSION_FAILURE:
            return {
                ...state,
                loading: false,
                errors:       action.payload.data || {},
                errorMessage: action.payload.message || "Failed to delete version",
            };

        // ── DOWNLOAD ─────────────────────────────────────────────────────
        case actionTypes.DOWNLOAD_EXT_VERSION_CSV_SUCCESS:
            return { ...state, downloading: false, errors: {}, errorMessage: "" };

        case actionTypes.DOWNLOAD_EXT_VERSION_CSV_FAILURE:
            return {
                ...state,
                downloading: false,
                errors:       action.payload.data || {},
                errorMessage: action.payload.message || "Failed to download CSV",
            };

        case actionTypes.INITIATE_DRAFT_SESSION_SUCCESS:
            return { ...state, loading: false, errors: {}, errorMessage: "" };

        case actionTypes.INITIATE_DRAFT_SESSION_FAILURE:
            return {
                ...state,
                loading: false,
                errors:       action.payload.data || {},
                errorMessage: action.payload.message || "Failed to initiate draft session",
            };
        case actionTypes.RECALCULATE_EXT_VERSION_SUCCESS:
            return { ...state, loading: false, errors: {}, errorMessage: "" };

        case actionTypes.RECALCULATE_EXT_VERSION_FAILURE:
            return {
                ...state,
                loading: false,
                errors:       action.payload.data || {},
                errorMessage: action.payload.message || "Failed to recalculate version",
            };

        default:
            return state;
    }
};

export default ExtVersions;